import { motion } from "framer-motion";
import { WashingMachine } from "lucide-react";
import TwinSilhouette from "@/components/TwinSilhouette";
import { EinsteinIcon } from "@/components/FlowIcons";

const INPUTS = [
  { label: "INPUT 01: YOUR TRIKARMA BAND", desc: "HRV • HEART RATE • SLEEP • STRESS • MOVEMENT • MORE" },
  { label: "INPUT 02: LIFESTYLE", desc: "MEALS • HYDRATION • ROUTINES • MEDITATION • BREATHWORK • GOALS • YOUR FEEDBACK" },
  { label: "INPUT 03: APP BEHAVIOUR", desc: "COMPLETED • SKIPPED • CHANGED • ASKED" },
  { label: "INPUT 04: CONTEXT", desc: "SCHEDULE • TRAVEL • ENVIRONMENT • AVAILABILITY" },
];

const MODELS = [
  { name: "MAD SCIENTIST", role: "ANALYSIS & EXPERIMENTATION", Icon: EinsteinIcon },
  { name: "WASHING MACHINE", role: "DATA CLEANING", Icon: WashingMachine },
];

const Drop = ({ pink }) => (
  <div className="mx-auto h-10 w-1">
    <svg
      viewBox="0 0 2 40"
      className="h-full w-full"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d="M1 0 L1 40" stroke="#2A2026" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      <path
        d="M1 0 L1 40"
        stroke={pink ? "#D2378B" : "#A19A9D"}
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        className="signal-flow"
      />
    </svg>
  </div>
);

const Rail = ({ side, color, style }) => (
  <svg
    className={`absolute ${side === "right" ? "right-0" : "left-0"} w-4`}
    style={style}
    viewBox="0 0 12 100"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <path
      d={side === "right" ? "M6 100 C 13 75 13 25 6 0" : "M6 100 C -1 75 -1 25 6 0"}
      stroke="#2A2026"
      strokeWidth="1"
      vectorEffect="non-scaling-stroke"
      fill="none"
    />
    <path
      d={side === "right" ? "M6 100 C 13 75 13 25 6 0" : "M6 100 C -1 75 -1 25 6 0"}
      stroke={color}
      strokeWidth="1"
      vectorEffect="non-scaling-stroke"
      fill="none"
      className="signal-flow"
    />
  </svg>
);

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay }}
  >
    {children}
  </motion.div>
);

const ArchitectureMobile = () => (
  <section
    data-testid="architecture-section-mobile"
    className="relative mx-auto max-w-xl px-6 py-28"
  >
    <p className="font-mono text-[11px] tracking-[0.28em] text-ash">02 · SYSTEM</p>
    <h2 className="mt-4 font-display text-3xl font-light text-white">
      How Trifit builds your digital twin
    </h2>

    {/* YOU */}
    <Reveal>
      <div className="mt-12 text-center">
        <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-white/30">
          <span className="pulse-ring absolute inset-0 rounded-full border border-white/40" />
          <div className="h-1.5 w-1.5 rounded-full bg-white" />
        </div>
        <p className="mt-3 font-mono text-[10px] tracking-[0.24em] text-white">YOU</p>
      </div>
      <Drop />
    </Reveal>

    {/* inputs */}
    <div className="space-y-3">
      {INPUTS.map((inp, i) => (
        <Reveal key={inp.label} delay={0.05 * i}>
          <div className="rounded-lg border border-line bg-ink-2/50 p-5">
            <p className="font-mono text-[10px] leading-relaxed tracking-[0.16em] text-white">
              {inp.label}
            </p>
            <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.1em] text-ash">
              {inp.desc}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal>
      <Drop />
    </Reveal>

    {/* intelligence -> twin -> plan with loop rails */}
    <div className="relative px-4">
      <Rail side="right" color="#D2378B" style={{ top: "3rem", height: "calc(100% - 6rem)" }} />
      <Rail side="left" color="#A19A9D" style={{ top: "3rem", height: "58%" }} />

      <Reveal>
        <div className="rounded-xl border border-white/25 bg-ink-2/60 p-5">
          <p className="font-mono text-[10px] tracking-[0.22em] text-white">
            TRIFIT INTELLIGENCE
          </p>
          <div className="tech-rule mt-3 w-full" />
          <div className="mt-4 grid grid-cols-2 gap-3">
            {MODELS.map(({ name, role, Icon }) => (
              <div
                key={name}
                className="flex flex-col items-center rounded-md border border-line px-2 py-4 text-center"
              >
                <Icon className="mb-2 h-4 w-4 shrink-0 text-signal" strokeWidth={1.5} />
                <p className="font-mono text-[9px] leading-relaxed tracking-[0.12em] text-fog">
                  {name}
                </p>
                <p className="mt-1 font-mono text-[8px] leading-relaxed tracking-[0.08em] text-ash">
                  {role}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-4 font-mono text-[8px] tracking-[0.16em] text-ash">
            MODEL: CONTINUOUS LEARNING
          </p>
        </div>
      </Reveal>

      <Reveal>
        <Drop pink />
      </Reveal>

      <Reveal>
        <div className="rounded-xl border border-line bg-ink-2/50 p-4">
          <TwinSilhouette className="h-56 w-full" />
          <p className="mt-2 text-center font-mono text-[10px] tracking-[0.2em] text-white">
            YOUR DIGITAL TWIN
          </p>
        </div>
      </Reveal>

      <Reveal>
        <Drop pink />
      </Reveal>

      <Reveal>
        <div className="rounded-lg border border-white/30 bg-ink-2 px-5 py-5 text-center">
          <p className="font-mono text-[10px] tracking-[0.2em] text-white">YOUR PLAN</p>
          <p className="mt-1 font-mono text-[8px] tracking-[0.12em] text-signal/80">
            OUTPUT: ADAPTIVE
          </p>
        </div>
      </Reveal>
    </div>

    <Reveal>
      <p className="mt-8 text-center font-mono text-[9px] leading-relaxed tracking-[0.18em] text-ash">
        EVERY OUTPUT <span className="text-signal">LOOPS BACK</span> INTO TRIFIT
        INTELLIGENCE · THE MODEL REFINES ON EVERY CYCLE
      </p>
    </Reveal>
  </section>
);

export default ArchitectureMobile;
